/* eslint-disable react-hooks/exhaustive-deps */
import "./Reports.scss";
import React, { FunctionComponent, useContext, useEffect, useRef, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import Button from "../../components/Button/Button";
import Dropdown from "../../components/Dropdown/Dropdown";
import { useLazyQuery } from "@apollo/client";
import {
  generateOnDemandReportQuery,
  getScheduledReportsQuery,
} from "./queries";
import InputField from "components/InputField/InputField";
import { displayError, displaySuccess, displayWarning } from "utils/displayNotification";
import CreateReport from "components/CreateReport/CreateReport";
import { CreateReportType, Marketplace, PermissionEnum, ReportSchedule, ScheduledReportType } from "TypesAndInterfaces";
import usePermission from "hooks/usePermission";
import { debounce } from "lodash";
import ReportsProvider, { ReportsContext } from "./Reports.context";
import DeleteReportForm from "components/DeleteReportForm/DeleteReportForm";

import { getMarketplacesQuery } from "pages/Marketplaces/queries";

const Reports: FunctionComponent = () => {
  const [isProviderMounted, setIsProviderMounted] = useState(false);

  useEffect(() => {
    setIsProviderMounted(true);
  }, []);

  return (
    <ReportsProvider>
      {isProviderMounted && <ReportsContent />}
    </ReportsProvider>
  );
};

const ReportsContent: FunctionComponent = () => {
  const initialReportState: CreateReportType = {
    id: undefined,
    sqlFunctionName: undefined,
    marketplaceId: undefined,
    hour: undefined,
    schedule: undefined,
    emails: "",
    name: ""
  };
  const [data, setData] = useState<CreateReportType[]>([]);
  const [total, setTotal] = useState(0);
  const [isCreateReportModalOpened, setIsCreateReportModalOpened] = useState(false);
  const [isDeleteReportModalOpened, setIsDeleteReportModalOpened] = useState(false);
  const { isAuthorized: canManageReports } = usePermission(PermissionEnum.ADMIN);
  const [index, setIndex] = useState(1);
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [numberOfReportsPerPage, setNumberOfReportsPerPage] = useState(10);
  const [marketplaceIdSearch, setMarketplaceIdSearch] = useState<number>();
  const [filteredData, setFilteredData] = useState<CreateReportType[]>([]);
  const selectMarketplaceRef = useRef(null);
  const [typeSearch, setTypeSearch] = useState<string>();
  const selectTypeeRef = useRef(null);

  const [reportToDelete, setReportToDelete] = useState<number>();
  const { setSelectedReport, setMarketplaceReports } = useContext(ReportsContext);

  const [, setOnDemandReportLoading] = useState(false);

  const marketplaceId = localStorage.getItem("marketplaceId");

  const [getScheduledReports, { loading }] = useLazyQuery(getScheduledReportsQuery, {
    fetchPolicy: "no-cache",
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      setData(data.getScheduledReports.scheduledReports);
      setTotal(data.getScheduledReports.totalCount);
    },
    onError: (e) => {
      displayError("Invalid records");
    },
  });

  const [getMarketplaces] = useLazyQuery<
    { getMarketplaces: { marketplaces: Marketplace[]; totalCount: number } },
    {
      pagination: { size: number; index: number };
      filter?: { enabled?: boolean; searchText?: string };
    }
  >(getMarketplacesQuery, {
    fetchPolicy: "no-cache",
    onCompleted: (dataMarketplaces) => {
      if (dataMarketplaces) {

        const { marketplaces } = dataMarketplaces?.getMarketplaces;
        const unique = new Map<string, string>();

        marketplaces.forEach((obj: any) => {
          unique.set(obj.id, obj.name);
        });

        setMarketplaceReports(Array.from(unique, ([marketplaceId, name]) => ({ marketplaceId, name })));

      }
    },
    onError: (error) => {
      displayError(`Something went wrong ${error.message}`);
    },
  });

  const [generateOnDemandReport] = useLazyQuery(generateOnDemandReportQuery, {
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      const base64Data = data.generateOnDemandReport;
      const blob = new Blob([s2ab(atob(base64Data))], { type: '' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const currentDate = new Date().toISOString().replace(/[-:]/g, '').slice(0, 14); // Get current date and time in YYYYMMDDHHmmss format
      a.download = `report_${currentDate}.xlsx`;
      a.click();
      setOnDemandReportLoading(false);
    },
    onError: (error) => {
      displayWarning(`${error.message}`);
      setOnDemandReportLoading(false);
    },
  });

  const s2ab = (s: string) => {
    var buf = new ArrayBuffer(s.length);
    var view = new Uint8Array(buf);
    for (var i = 0; i !== s.length; ++i) view[i] = s.charCodeAt(i) & 0xFF;
    return buf;
  }

  const handleFetchReports = () => {
    getScheduledReports({
      variables: {
        filter: { reportName: searchText },
        pagination: {
          index,
          size: numberOfReportsPerPage,
        },
      }
    });
    if (!marketplaceId) {
      getMarketplaces();
    }
  };

  useEffect(() => {
    handleFetchReports();
  }, []);

  useEffect(() => {
    if (reportToDelete) {
      setIsDeleteReportModalOpened(true);
    }
  }, [reportToDelete]);

  const getReport = (id: number) => {
    return data.find((report: any) => { return report.id === id }) ?? initialReportState
  }

  const getColumnsReports = () => {
    if (!canManageReports) {
      columns.length = columns.length - 2;
    }
    return columns;
  };

  const columns: TableColumn<any>[] = [
    {
      name: "ID",
      selector: (row) => row.id,
      width: "80px",
    },
    {
      name: "Marketplace",
      selector: (row) => row.marketplaceName || "N/A",
    },
    {
      name: "Type",
      selector: (row) => (
        <div>
          <div>{row.name}</div>
        </div>
      ),
    },
    {
      name: "Emails Count",
      width: "150px",
      selector: (row) => {
        if (row.schedule === ReportSchedule.OnDemand) {
          return "-";
        }
        return row.emails.length > 0 ? row.emails.split(',').length : 0;
      },
    },
    {
      name: "Schedule",
      selector: (row) => {
        if (row.schedule === ReportSchedule.OnDemand) {
          return "On Demand";
        }
        return row.hour + "-" + row.schedule.replace(/([a-z])([A-Z])/g, '$1 $2');
      },
    },
    {
      name: "ACTIONS",
      cell: (row) => (
        <>
          <Button
            icon="pencil"
            type="button"
            children={undefined}
            className={"button--circle"}
            onClick={() => {
              setIsCreateReportModalOpened(true);
              setSelectedReport(getReport(row.id));
            }}
            content={undefined}
          />
          <Button
            icon="trash"
            type="button"
            children={undefined}
            className={"button--circle"}
            onClick={() => {
              setSelectedReport(getReport(row.id));
              setReportToDelete(row.id);
            }}
            content={undefined}
          />
          {row.schedule === ReportSchedule.OnDemand && (
            <Button
              icon="download"
              type="button"
              children={undefined}
              className={"button--circle"}
              content={undefined}
              onClick={() => {
                setOnDemandReportLoading(true);
                generateOnDemandReport({
                  variables: {
                    id: row.id,
                  },
                });
              }}
            />
          )}
        </>
      ),
    },
  ];

  const changePage = (page: number, totalRows: any) => {
    setIndex(page);
  };

  const changeRowsPerPage = (
    currentRowsPerPage: number,
    currentPage: number
  ) => {
    setNumberOfReportsPerPage(currentRowsPerPage);
    setIndex(currentPage);
  };

  const handleSearch = debounce((searchValue: string) => {
    setSearchText(searchValue);
    fetchReports(searchValue);
  }, 100);

  const handleSearchMarketplace = () => {
    const filterData = data.filter((item: CreateReportType) => {
      return (!marketplaceIdSearch || item.marketplaceId === marketplaceIdSearch) &&
        (!typeSearch || item.sqlFunctionName === typeSearch);
    });
    if (filterData) {
      setFilteredData(filterData);
      displaySuccess("Filters applied correctly")
    } else {
      setFilteredData(data)
    }
  }

  const fetchReports = async (searchValue?: string) => {
    let filter = {};
    const value = searchValue !== undefined ? searchValue : searchText;

    filter = { reportName: value };

    await getScheduledReports({
      variables: {
        pagination: { size: numberOfReportsPerPage, index },
        filter
      },
    });
  }

  useEffect(() => {
    fetchReports();
  }, [index, numberOfReportsPerPage]);

  const resetFilters = () => {
    setFilteredData(data);
    setIndex(1);
    setResetPaginationToggle(!resetPaginationToggle);
    setTypeSearch(undefined);
    setMarketplaceIdSearch(undefined);
    (selectMarketplaceRef.current as any).value = "";
    (selectTypeeRef.current as any).clearValue();
  };

  useEffect(() => {
    setFilteredData(data);
  }, [data])


  return (
    <div className="content-wrapper reports_container">
      <main className="main-container-wrapper">
        <section className="main-container">
          <header className="main-container__header">
            <h2 className="main-container__title">Reports</h2>
            <div className="main-container__search-container">
              <InputField
                type="search"
                icon="search"
                className="no-margin"
                onChange={(e) => {
                  handleSearch(e.target.value)
                }}
                placeholder="Enter your search term"
              />
            </div>
          </header>
          <div className={"content-wrapper"}>
            <CreateReport
              initialReportState={initialReportState}
              fetchReports={fetchReports}
              isCreateReportModalOpened={isCreateReportModalOpened}
              setIsCreateReportModalOpened={setIsCreateReportModalOpened}
              data={filteredData}
            />
            <DeleteReportForm
              isDeleteReportModalOpened={isDeleteReportModalOpened}
              setIsDeleteReportModalOpened={setIsDeleteReportModalOpened}
              fetchReportss={fetchReports}
            />
          </div>
          <div className="main-content" style={{ flexWrap: "unset" }}>
            <aside className="main-content__sidebar">
              <header className="main-content__sidebar__header">
                <h4 className="main-content__sidebar__title">Filters</h4>
                <Button
                  className="button--small button--rounded button--no-wrap"
                  onClick={() => resetFilters()}
                  type="button"
                  content="RESET ALL"
                ></Button>
              </header>
              <div className="filters">
                <Dropdown
                  options={[{ value: ScheduledReportType.GetInvoicesStatusReport, label: "Invoice Status Report" },
                  { value: ScheduledReportType.GetInvoicesLastMonthStatusReport, label: "Invoice Last Month Status Report" },
                  { value: ScheduledReportType.GetShopKycStatusReport, label: "Shop KYC Status Report" }]}
                  placeholder={""}
                  label={"Type"}
                  innerRef={selectTypeeRef}
                  onChange={(e: any) => e?.value && setTypeSearch(e?.value)}
                />
                <InputField
                  innerRef={selectMarketplaceRef}
                  type="search"
                  icon="search"
                  className="no-margin"
                  label={"Marketplace Id"}
                  onChange={(e) => {
                    setMarketplaceIdSearch(+e.currentTarget.value);
                  }}
                />
                <Button
                  className="button--primary"
                  content="Apply Filters"
                  onClick={() => {
                    handleSearchMarketplace();
                  }}
                />
              </div>
            </aside>
            <div className="main-content__result">
              {loading ? (
                <div className="adyen-loader" />
              ) : (
                <DataTable
                  pagination
                  paginationServer
                  paginationTotalRows={total}
                  paginationPerPage={numberOfReportsPerPage}
                  paginationRowsPerPageOptions={[10, 25, 50, 100]}
                  onChangeRowsPerPage={changeRowsPerPage}
                  onChangePage={changePage}
                  columns={getColumnsReports()}
                  data={filteredData}
                  paginationDefaultPage={index}
                  paginationResetDefaultPage={resetPaginationToggle}
                  style={{ tableLayout: "fixed", width: "100%" }}
                />
              )}
            </div>
          </div>
          <div className="bottom-button-container">
            <Button
              className={"button--icon-only button--add"}
              type="button"
              content={"+"}
              icon={""}
              onClick={() => {
                setIsCreateReportModalOpened(true)
                setSelectedReport({
                  id: undefined,
                  sqlFunctionName: undefined,
                  marketplaceId: undefined,
                  hour: undefined,
                  schedule: undefined,
                  emails: "",
                  name: ""
                })
              }}
            />
          </div>
        </section>
      </main>
    </div>
  );
};

export default Reports;
